import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-4 py-3" data-testid="typing-indicator">
      <span className="typing-dot w-2 h-2 rounded-full bg-fuchsia-400" style={{ animationDelay: "0s" }} />
      <span className="typing-dot w-2 h-2 rounded-full bg-violet-400" style={{ animationDelay: "0.2s" }} />
      <span className="typing-dot w-2 h-2 rounded-full bg-cyan-400" style={{ animationDelay: "0.4s" }} />
    </div>
  );
}

function Msg({ role, content, avatar }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`message-${role}`}>
      {!isUser && (
        <img src={avatar} alt="bot" className="w-9 h-9 rounded-full object-cover border border-white/10 flex-shrink-0" />
      )}
      <div
        className={`max-w-[80%] p-4 leading-relaxed text-sm sm:text-[15px] whitespace-pre-wrap ${
          isUser
            ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-2xl rounded-tr-sm shadow-lg shadow-indigo-500/20"
            : "bg-[#181B29] text-slate-200 rounded-2xl rounded-tl-sm border border-white/5"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

export default function Chat() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [character, setCharacter] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const scrollRef = useRef(null);

  const expiryWarnings = useMemo(() => {
    if (!user) return [];
    const now = Date.now();
    const soon = 24 * 60 * 60 * 1000;
    const out = [];
    const hoursLeft = (iso) => Math.max(1, Math.ceil((new Date(iso).getTime() - now) / 3600000));
    if (user.perk_unlimited && user.perk_unlimited_until) {
      const t = new Date(user.perk_unlimited_until).getTime() - now;
      if (t > 0 && t < soon) out.push({ key: "unlimited", label: "Unbegrenzte Nachrichten", hours: hoursLeft(user.perk_unlimited_until) });
    }
    if (user.is_vip && user.vip_until && user.role === "user") {
      const t = new Date(user.vip_until).getTime() - now;
      if (t > 0 && t < soon) out.push({ key: "vip", label: "VIP-Zugang", hours: hoursLeft(user.vip_until) });
    }
    return out;
  }, [user]);

  useEffect(() => {
    api
      .get(`/chats/${sessionId}/messages`)
      .then((res) => {
        setCharacter(res.data.character);
        setMessages(res.data.messages);
      })
      .catch(() => {
        toast.error("Chat not found");
        navigate("/");
      });
  }, [sessionId, navigate]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamText, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content: text }]);
    setStreaming(true);
    setStreamText("");

    try {
      const token = localStorage.getItem("jb_token");
      const res = await fetch(`${API}/chats/${sessionId}/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: text }),
      });

      if (!res.ok) {
        let detail = "Message failed";
        try {
          const body = await res.json();
          if (typeof body.detail === "string") detail = body.detail;
        } catch (_) {}
        setMessages((m) => m.filter((x) => x.content !== text || x.role !== "user"));
        setStreaming(false);
        toast.error(detail);
        if (res.status === 402) navigate("/coins");
        else if (res.status === 429 || res.status === 403) navigate("/vip");
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = JSON.parse(line.slice(5).trim());
          if (payload.delta) {
            acc += payload.delta;
            setStreamText(acc);
          } else if (payload.error) {
            toast.error(payload.error);
          }
        }
      }

      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: acc }]);
      refreshUser().catch(() => {});
    } catch (e) {
      toast.error("Message failed");
    } finally {
      setStreaming(false);
      setStreamText("");
    }
  };

  if (!character) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#090A0F] text-slate-400">
        Loading chat...
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-[#090A0F] grid-bg">
      {/* Header */}
      <header className="glass border-b border-white/5 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-3">
          <button
            data-testid="chat-back-button"
            onClick={() => navigate("/")}
            className="p-2 rounded-full hover:bg-white/10 text-slate-300 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <img
            src={character.avatar_url}
            alt={character.name}
            className="w-10 h-10 rounded-full object-cover border border-white/10"
          />
          <div className="min-w-0">
            <h1 className="font-display font-bold text-white leading-tight truncate" data-testid="chat-character-name">
              {character.name}
            </h1>
            <p className="text-xs text-slate-400 truncate">{character.title}</p>
          </div>
          <span className="ml-auto flex items-center gap-1.5 text-xs text-emerald-400">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Online
          </span>
        </div>
      </header>

      {expiryWarnings.length > 0 && (
        <div className="bg-amber-500/10 border-b border-amber-400/20 flex-shrink-0" data-testid="chat-expiry-warning">
          <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center gap-2 text-amber-300 text-xs sm:text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              {expiryWarnings.map((w) => `${w.label} läuft in ${w.hours} Std. ab`).join(" · ")}
              {expiryWarnings.some((w) => w.key === "vip") && (
                <button
                  data-testid="chat-expiry-renew"
                  onClick={() => navigate("/vip")}
                  className="ml-2 underline font-semibold hover:text-amber-200"
                >
                  Verlängern
                </button>
              )}
            </span>
          </div>
        </div>
      )}

      {character.gallery && character.gallery.length > 0 && (
        <div className="glass border-b border-white/5 flex-shrink-0" data-testid="chat-gallery">
          <div className="max-w-3xl mx-auto px-4 py-2.5 flex gap-2 overflow-x-auto">
            {character.gallery.map((g, i) => (
              <img
                key={i}
                src={g}
                alt={`gallery ${i}`}
                className="w-12 h-12 rounded-lg object-cover border border-white/10 flex-shrink-0 hover:scale-105 transition-transform"
              />
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
          {messages.map((m) => (
            <Msg key={m.id} role={m.role} content={m.content} avatar={character.avatar_url} />
          ))}
          {streaming && (
            <div className="flex gap-3" data-testid="streaming-message">
              <img
                src={character.avatar_url}
                alt="bot"
                className="w-9 h-9 rounded-full object-cover border border-white/10 flex-shrink-0"
              />
              {streamText ? (
                <div className="max-w-[80%] p-4 leading-relaxed text-sm sm:text-[15px] whitespace-pre-wrap bg-[#181B29] text-slate-200 rounded-2xl rounded-tl-sm border border-white/5">
                  {streamText}
                </div>
              ) : (
                <div className="bg-[#181B29] rounded-2xl rounded-tl-sm border border-white/5">
                  <TypingDots />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="glass border-t border-white/5 flex-shrink-0">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-end gap-3">
          <div className="flex-1 flex items-center gap-2 bg-[#131520] border border-white/10 focus-within:border-fuchsia-500/50 rounded-2xl px-4 py-1 transition-all">
            <Sparkles className="w-4 h-4 text-fuchsia-400 flex-shrink-0" />
            <textarea
              data-testid="chat-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={`Message ${character.name}...`}
              className="flex-1 bg-transparent text-white placeholder-slate-500 outline-none resize-none py-2.5 max-h-32"
            />
          </div>
          <button
            data-testid="chat-send-button"
            onClick={send}
            disabled={streaming || !input.trim()}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-500/25 active:scale-95 transition-all disabled:opacity-40 flex-shrink-0"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
